import { Injectable, inject } from '@angular/core';
import { forkJoin, merge, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, map, mapTo, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { keywordAllowedPattern } from '@app/config';
import { Country, Period } from '@shared/models';
import { PeriodHelpers } from '@shared/helpers/period.helpers';
import { PersistedSettingsService, SETTINGS } from '@shared/services';
import { FiltersService } from '@shared/services/filters/filters.service';
import { REPORT_CONFIG } from '@shared/const/report-config';
import { RouteIdsItem } from '@main/types/types';
import { EntityHelpers } from '@main/helpers/entity.helpers';
import { Device, EntityGroup, EntityItem } from '@main/models';
import { EntityService } from '@main/services/entity.service';
import { ReportApiService } from '@main/core/report.api-service';
import { DatasetsData } from '@main/core/report.types';
import { ReportService } from '@main/core/report.service';
import { EntityCommonService } from '@main/services/entity-common.service';
import { CampaignIdsPayload } from './keywords.service.types';
import { KeywordsApiService } from './keywords.api-service';
import { KeywordsHelpers } from '../keywords.helpers';

@UntilDestroy()
@Injectable()
export class KeywordsEntityService extends EntityService {
    protected readonly entityCommonService = inject(EntityCommonService);
    protected readonly reportService = inject(ReportService);
    protected readonly reportConfig = inject(REPORT_CONFIG);
    protected readonly persistedSettingsService = inject(PersistedSettingsService);
    protected readonly reportApiService = inject(ReportApiService);
    protected readonly filtersService = inject(FiltersService);

    private readonly apiService = inject(KeywordsApiService);

    private getCampaignsIdsByKeywords$ = new ReplaySubject<CampaignIdsPayload>(1);

    private campaignsIds: number[] = [];
    private campaignsIdsTotal = 0;

    campaignIds$ = merge(
        this.reportService.reportTokenUpdateTrigger$.pipe(map(() => null)),

        this.reportService.reportTokenUpdateTrigger$.pipe(
            withLatestFrom(this.filtersService.value$, this.reportService.entities$),
            map(([, ...rest]) => rest),
            switchMap(([filters, entities]) => this.cachedFetchCampaignsIds({ filters, entities })),
        ),
    );

    campaignsCount$ = this.campaignIds$.pipe(
        filter(Boolean),
        map((res) => res[Symbol.for('total')] || 0),
        shareReplay(1),
    );

    loading$ = merge(
        this.getCampaignsIdsByKeywords$.pipe(mapTo(true)),
        this.campaignIds$.pipe(
            mapTo(false),
            catchError(() => of(false)),
        ),
    );

    loadingCount$ = merge(
        this.filtersService.value$.pipe(mapTo(true)),
        this.campaignsCount$.pipe(
            mapTo(false),
            catchError(() => of(false)),
        ),
    );

    constructor() {
        super();

        this.reportService.reportUpdateTrigger$.subscribe(() => {
            this.campaignsIds = [];
            this.campaignsIdsTotal = 0;
        });
    }

    getDatasetsData(): Observable<DatasetsData> {
        return forkJoin([
            this.reportApiService.getChannels(),
            this.reportApiService.getPeriods(),
            this.reportApiService.getCountries(),
        ]).pipe(map(([channels, periods, countries]) => ({ channels, periods, countries })));
    }

    parseRouteIds(str: string): RouteIdsItem[] {
        const r = new RegExp('/(.+?)|([^,]+?:)|(' + keywordAllowedPattern + ')|([a-z0-9-]+)', 'gui');
        const parsed = str.match(r);
        let result: RouteIdsItem[] = [];

        for (const v of parsed) {
            /* virtual group name */
            if (v.slice(-1) === ':') {
                result = this.parseRouteIds(parsed.slice(1).join(','));
                result[Symbol.for('name')] = v.slice(0, -1);

                break;
            }

            result.push(v);
        }

        return result;
    }

    resolveEntitiesByIds<T>(ids: RouteIdsItem[]): Observable<T[]> {
        const groupsIds = ids.filter((id) => EntityHelpers.isGroupId(id)) as string[];

        return this.entityCommonService
            .resolveGroupsByIds(groupsIds)
            .pipe(map((groupsEntities) => this.buildEntitiesTree(ids, groupsEntities))) as unknown as Observable<T[]>;
    }

    getDefaultDevice(devices: Device[]): Device {
        const persistedId = this.persistedSettingsService.get<string>(SETTINGS.ROOT_DEVICE);

        let device = devices.find((v) => v.code === persistedId && !v.disabled && !v.restricted);

        if (!device) {
            device = devices && devices[0];
        }

        if (devices?.length === 1) {
            device.disabled = true;
        }

        return device;
    }

    getDefaultCountry(countries: Country[]): Country {
        const persistedId = this.persistedSettingsService.get<number>(SETTINGS.ROOT_COUNTRY);

        let bestCountry = countries.find((v) => v.id === persistedId && !v.disabled && !v.restricted);

        if (!bestCountry) {
            const allAvaliableCountries = countries.filter((v) => !v.restricted && !v.disabled);

            bestCountry = allAvaliableCountries[0];
        }

        return bestCountry;
    }

    getDefaultPeriod(periods: Period[]): Period {
        const persistedId = this.persistedSettingsService.get<string>(SETTINGS.ROOT_PERIOD);

        const availableperiods = periods.filter((v) => !v.restricted);

        let period: Period;

        if (persistedId) {
            period = PeriodHelpers.getPeriodByPersistedValue(periods, persistedId);
        }

        if (!period) {
            period = PeriodHelpers.calculateWholeRangePeriod(periods);
        }

        if (!period) {
            period =
                availableperiods.find((v) => v.code === 'LAST_30_DAYS') ||
                availableperiods.find((v) => v.code === 'LAST_7_DAYS');
        }

        if (!period) {
            period = availableperiods[0];
        }

        return period;
    }

    buildEntitiesTree(ids: RouteIdsItem[], groupsEntities: EntityGroup[]): EntityItem[] {
        return ids
            .map((id) => {
                const isGroup = EntityHelpers.isGroupId(id);

                if (isGroup) {
                    const group = groupsEntities.find((v) => v.id === id);

                    if (!group) {
                        return;
                    }

                    group.items = KeywordsHelpers.createKeywordsEntities(group._dto.details.instances);
                    group.setResolved();

                    return group;
                }

                return KeywordsHelpers.createKeywordEntity(id);
            })
            .filter(Boolean);
    }

    cachedFetchCampaignsIds({
        filters,
        entities,
        pageNumber = 0,
        pageSize = 100,
    }: CampaignIdsPayload): Observable<number[]> {
        const values = this.campaignsIds.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);

        /* The cached page must carry the total as well: consumers paginate by it, and a missing marker reads as
         * zero, which cuts the pagination short. The total is known only once a page has been fetched, and both
         * the ids and the total are reset together on every report update. */
        if (values.length === pageSize && this.campaignsIdsTotal) {
            values[Symbol.for('total')] = this.campaignsIdsTotal;

            return of(values);
        }

        const campaignIds$ = this.apiService
            .fetchCampaignsIds({ filters, entities, pageNumber, pageSize })
            .pipe(shareReplay(1));

        campaignIds$.pipe(untilDestroyed(this)).subscribe((ids) => {
            this.campaignsIdsTotal = ids[Symbol.for('total')] || 0;

            ids.forEach((id, index) => (this.campaignsIds[pageNumber * pageSize + index] = id));
        });

        return campaignIds$;
    }
}
